import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, message } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import tokenExpirationHandler from '../../utils/tokenExpirationHandler';
import apiService from '../../utils/api';

const TokenExpirationChecker: React.FC = () => {
  const { accessToken, logout, refreshAccessToken } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const warningShownRef = useRef(false);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!accessToken) return;

    const checkTokenExpiration = () => {
      const timeUntilExpiration = tokenExpirationHandler.getTimeUntilExpiration(accessToken);
      const isExpired = tokenExpirationHandler.isTokenExpired(accessToken);

      if (isExpired) {
        // Token is expired, handle immediately
        tokenExpirationHandler.handleTokenExpiration();
        return;
      }

      // Warn user 5 minutes before expiration
      const fiveMinutesInMs = 5 * 60 * 1000;
      if (timeUntilExpiration <= fiveMinutesInMs && timeUntilExpiration > 0 && !warningShownRef.current) {
        warningShownRef.current = true;
        
        const modal = Modal.confirm({
          title: 'Session Expiring Soon',
          content: 'Your session will expire in less than 5 minutes. Would you like to extend your session?',
          okText: isRefreshing ? 'Extending...' : 'Extend Session',
          cancelText: 'Logout Now',
          onOk: async () => {
            try {
              console.log('User clicked Extend Session');
              setIsRefreshing(true);
              const success = await refreshAccessToken();
              console.log('Token refresh result:', success);
              if (success) {
                message.success('Session extended successfully!');
                warningShownRef.current = false; // Reset warning flag
                modal.destroy(); // Close the modal
              } else {
                message.error('Failed to extend session. Please log in again.');
                logout();
              }
            } catch (error) {
              console.error('Error refreshing token:', error);
              message.error('Failed to extend session. Please log in again.');
              logout();
            } finally {
              setIsRefreshing(false);
            }
          },
          onCancel: () => {
            logout();
          },
          onClose: () => {
            // If user closes the modal, still logout after a short delay
            setTimeout(() => {
              if (tokenExpirationHandler.isTokenExpired(accessToken)) {
                tokenExpirationHandler.handleTokenExpiration();
              }
            }, 1000);
          }
        });
      }
    };

    // Check immediately
    checkTokenExpiration();

    // Check every 30 seconds
    checkIntervalRef.current = setInterval(checkTokenExpiration, 30000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [accessToken, logout]);

  // Reset warning flag when token changes
  useEffect(() => {
    warningShownRef.current = false;
  }, [accessToken]);

  return null; // This component doesn't render anything
};

export default TokenExpirationChecker; 